import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { FiPlus, FiSearch } from 'react-icons/fi';
import TechTaskModal from '../components/TechTaskModal';
import TechPagination from '../components/TechPagination';
import TechUserFilter from '../components/TechUserFilter';
import { TYPE_COLORS, STATUS_COLORS, formatDateTimeShort } from '../utils/techTask';

const PAGE_SIZE = 10;

const TechTasks = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [status, setStatus] = useState('all');
  const [assignee, setAssignee] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });

  const fetchTasks = async (p = page) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(p),
        limit: String(PAGE_SIZE),
      });
      if (status !== 'all') params.set('status', status);
      if (assignee !== 'all') params.set('assigned_to', assignee);
      if (search) params.set('search', search);
      const res = await api.get(`/tech-tasks?${params.toString()}`);
      setTasks(res.data.tasks || []);
      setPagination(res.data.pagination || { total: 0, totalPages: 1, page: p });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks(page);
  }, [status, assignee, page]);

  const handleStatusChange = (s) => {
    setStatus(s);
    setPage(1);
  };

  const handleAssigneeChange = (id) => {
    setAssignee(id);
    setPage(1);
  };

  const handleSearch = () => {
    setPage(1);
    fetchTasks(1);
  };

  return (
    <div className="space-y-4 max-w-6xl pb-8">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-white">Tickets</h1>
        <button
          type="button"
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-teal-500 text-slate-950 font-semibold text-sm touch-manipulation"
        >
          <FiPlus size={18} />
          New
        </button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Search title, client…"
            className="w-full pl-10 pr-4 py-3 rounded-xl bg-slate-900 border border-slate-800 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500/40"
          />
        </div>
        <TechUserFilter value={assignee} onChange={handleAssigneeChange} />
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
        {['all', 'pending', 'in_progress', 'completed', 'blocked'].map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => handleStatusChange(s)}
            className={`shrink-0 px-3 py-2 rounded-lg text-xs font-medium capitalize ${
              status === s ? 'bg-teal-500 text-slate-950' : 'bg-slate-900 text-slate-400 border border-slate-800'
            }`}
          >
            {s.replace('_', ' ')}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-400" />
        </div>
      ) : tasks.length === 0 ? (
        <p className="text-center text-slate-500 py-12">No tickets match.</p>
      ) : (
        <>
          <div className="rounded-xl border border-slate-800 bg-slate-900 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] text-sm">
                <thead>
                  <tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wide text-slate-500">
                    <th className="px-4 py-3 font-medium">Title</th>
                    <th className="px-4 py-3 font-medium">Client</th>
                    <th className="px-4 py-3 font-medium">Type</th>
                    <th className="px-4 py-3 font-medium">Assignee</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Start</th>
                    <th className="px-4 py-3 font-medium">End</th>
                    <th className="px-4 py-3 font-medium">Time taken</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {tasks.map((t) => (
                    <tr key={t.id} className="hover:bg-slate-800/50 transition-colors">
                      <td className="px-4 py-3">
                        <Link to={`/tech/tasks/${t.id}`} className="font-medium text-white hover:text-teal-300">
                          {t.title}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-slate-400">{t.client_name || '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`${TYPE_COLORS[t.task_type] || ''} text-[10px] px-2 py-0.5 rounded-full capitalize`}>
                          {t.task_type}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-300 whitespace-nowrap">
                        {t.assigned_to_name || '—'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`${STATUS_COLORS[t.status] || 'bg-slate-600'} text-white text-[10px] px-2 py-0.5 rounded-full capitalize whitespace-nowrap`}>
                          {t.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-400 whitespace-nowrap text-xs">
                        {formatDateTimeShort(t.start_at)}
                      </td>
                      <td className="px-4 py-3 text-slate-400 whitespace-nowrap text-xs">
                        {formatDateTimeShort(t.end_at)}
                      </td>
                      <td className="px-4 py-3 text-teal-400 whitespace-nowrap text-xs">
                        {t.duration ? (
                          <span title={`${t.duration.totalHours} billable hrs`}>
                            {t.duration.label}
                          </span>
                        ) : t.start_at && !t.end_at ? (
                          <span className="text-slate-500">In progress</span>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <TechPagination
            page={page}
            totalPages={pagination.totalPages}
            total={pagination.total}
            onPageChange={setPage}
          />
        </>
      )}

      {showModal && (
        <TechTaskModal
          onClose={() => setShowModal(false)}
          onSuccess={() => {
            setShowModal(false);
            setPage(1);
            fetchTasks(1);
          }}
        />
      )}
    </div>
  );
};

export default TechTasks;
